import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Smartphone } from "lucide-react";
import { AppSelfTest, SELF_TEST_LABELS, type SelfTestKey } from "@/app-bridge/components/AppSelfTest";
import {
  getAppModeStats,
  getAppPurchaseSetting,
  setAppPurchaseSetting,
} from "@/app-bridge/functions/app-mode.functions";

export const Route = createFileRoute("/_authenticated/admin/applage")({
  head: () => ({
    meta: [
      { title: "Appläge — Sellavo admin" },
      {
        name: "description",
        content:
          "Status och statistik för Sellavo i mobilappen: kamera, film, plats, vibration och hur många som använder appen.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ApplagePage,
});

const FEATURE_LABELS = SELF_TEST_LABELS;

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-2xl font-bold">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function List({ title, rows }: { title: string; rows: { key: string; count: number }[] }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="font-heading text-sm font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">Inget att visa än.</p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm">
          {rows.map((r) => (
            <li key={r.key} className="flex items-center justify-between gap-3">
              <span className="truncate text-muted-foreground">{r.key}</span>
              <span className="font-medium tabular-nums">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ApplagePage() {
  const queryClient = useQueryClient();
  const loadStats = useServerFn(getAppModeStats);
  const loadSetting = useServerFn(getAppPurchaseSetting);
  const saveSetting = useServerFn(setAppPurchaseSetting);

  const [days, setDays] = useState(30);
  const [inApp, setInApp] = useState(false);
  const [device, setDevice] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    void import("@/app-bridge/core/wrapper").then(async (m) => {
      setInApp(m.isAppMode());
      const info = await m.primeDeviceInfo();
      if (info) setDevice(info as Record<string, string>);
    });
  }, []);

  const stats = useQuery({
    queryKey: ["app-mode-stats", days],
    queryFn: () => loadStats({ data: { days } }),
  });

  const setting = useQuery({
    queryKey: ["app-purchase-setting"],
    queryFn: () => loadSetting(),
  });

  const toggle = useMutation({
    mutationFn: (hidden: boolean) => saveSetting({ data: { hidden } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["app-purchase-setting"] }),
  });

  const data = stats.data;

  return (
    <AdminShell title="Appläge">
      <div className="space-y-6">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Smartphone className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <p className="font-heading text-sm font-semibold">
                {inApp ? "Du är inne i mobilappen" : "Du tittar via en vanlig webbläsare"}
              </p>
              <p className="text-xs text-muted-foreground">
                {inApp
                  ? `${device?.["model"] ?? "Okänd enhet"} · ${device?.["system"] ?? "okänt system"} · appversion ${device?.["appVersion"] ?? "okänd"}`
                  : "Självtesterna nedan kan bara köras i appen. Sajten fungerar precis som vanligt här."}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-heading text-base font-semibold">Självtest</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Kör testerna i appen för att se att kamera, film, bilder, plats, biometri och övriga
            native-funktioner svarar. Allt faller automatiskt tillbaka till webbens lösning om något
            inte svarar.
          </p>
          <div className="mt-4">
            <AppSelfTest />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-base font-semibold">Köp i appen</h2>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Slå på detta när appen ligger i App Store och köp måste ske via Apple. Då döljs köp-
                och uppgraderingsknappar för den som surfar inne i appen, medan webben är
                oförändrad.
              </p>
            </div>
            <Switch
              checked={Boolean(setting.data?.hidden)}
              disabled={setting.isLoading || toggle.isPending}
              onCheckedChange={(v) => toggle.mutate(v)}
              aria-label="Dölj köpknappar i appen"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? "default" : "outline"}
              onClick={() => setDays(d)}
            >
              {d} dagar
            </Button>
          ))}
        </div>

        {stats.isLoading ? (
          <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Hämtar statistik …
          </div>
        ) : data ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Besökare i appen"
                value={String(data.appVisitors)}
                hint={`${data.webVisitors} på webben`}
              />
              <StatCard label="Händelser i appen" value={String(data.appEvents)} />
              <StatCard
                label="Andel i appen"
                value={`${(data.appShare * 100).toFixed(1)} %`}
                hint="av all uppmätt aktivitet"
              />
              <StatCard label="Totalt uppmätt" value={String(data.totalEvents)} />
            </div>

            <div className="grid gap-3 lg:grid-cols-3">
              <List title="Appversioner" rows={data.versions} />
              <List title="Enheter" rows={data.devices} />
              <List title="Systemversioner" rows={data.systems} />
            </div>

            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="font-heading text-sm font-semibold">Nativa funktioner</h3>
              {data.features.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  Ingen har använt appens kamera, film eller plats ännu.
                </p>
              ) : (
                <table className="mt-2 w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="py-1">Funktion</th>
                      <th className="py-1 text-right">Lyckade</th>
                      <th className="py-1 text-right">Föll tillbaka</th>
                      <th className="py-1 text-right">Senaste orsak</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.features.map((f) => (
                      <tr key={f.feature} className="border-t border-border">
                        <td className="py-1">
                          {FEATURE_LABELS[f.feature as SelfTestKey] ?? f.feature}
                        </td>
                        <td className="py-1 text-right tabular-nums">{f.ok}</td>
                        <td className="py-1 text-right tabular-nums">{f.fallback}</td>
                        <td className="py-1 text-right text-xs text-muted-foreground">
                          {f.lastReason || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Kunde inte hämta statistiken just nu.
          </div>
        )}
      </div>
    </AdminShell>
  );
}
